import { Injectable } from '@angular/core';
import { Floor, FloorsService } from '@era/locations-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { compare } from 'fast-json-patch';
import { mergeMap } from 'rxjs/operators';

import { FloorResolverActions, FloorApiActions, FloorListActions } from './floor.actions';
import { safeMap } from '../effects.helpers';

// ============================================================================
// FLOOR EFFECTS
// ============================================================================
// Note: We will step away from using these effects in the future, so don't
// add new ones or build upon them.
// ============================================================================

@Injectable()
export class FloorEffects {
  constructor(
    private readonly floorsService: FloorsService,
    private readonly actions$: Actions
  ) {}

  floorsLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FloorResolverActions.floorsRequested),
      mergeMap(({ locationId }) =>
        safeMap(
          this.floorsService.getFloors(locationId),
          (floors: Floor[]) => FloorApiActions.floorsLoaded({ floors }),
          (error) => FloorApiActions.floorsLoadedError({ error })
        )
      )
    );
  });

  floorAddedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FloorListActions.floorAdded),
      mergeMap(({ locationId, floor }) =>
        safeMap(
          this.floorsService.addFloor(locationId, floor),
          (floor: Floor) => FloorApiActions.floorAdded({ floor }),
          (error) => FloorApiActions.floorAddedError({ floor, error })
        )
      )
    );
  });

  floorDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FloorListActions.floorDeleted),
      mergeMap(({ floor }) =>
        safeMap(
          this.floorsService.deleteFloor(floor.locationId, floor.id),
          () => FloorApiActions.floorDeleted({ floor }),
          (error) => FloorApiActions.floorDeletedError({ floor, error })
        )
      )
    );
  });

  floorUpdatedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FloorListActions.floorUpdated),
      mergeMap(({ originalFloor, floor }) => {
        const update = compare(originalFloor, floor);

        return safeMap(
          this.floorsService.updateFloor(floor.locationId, floor.id, update),
          () => FloorApiActions.floorUpdated({ floor }),
          (error) => FloorApiActions.floorUpdatedError({ floor, error })
        );
      })
    );
  });
}
